import { toState } from '../redux/functions';
import VietMeetJS from './_';
const VietMeetConferenceErrors = VietMeetJS.errors.conference;
const VietMeetConnectionErrors = VietMeetJS.errors.connection;
/**
 * Creates a {@link VietMeetLocalTrack} model from the given device id.
 *
 * @param {string} type - The media type of track being created. Expected values
 * are "video" or "audio".
 * @param {string} deviceId - The id of the target media source.
 * @param {number} [timeout] - A timeout for the VietMeetJS.createLocalTracks function call.
 * @param {Object} additionalOptions - Extra options to be passed to vietmeet-lib's {@code createLocalTracks}.
 *
 * @returns {Promise<VietMeetLocalTrack>}
 */
export function createLocalTrack(type, deviceId, timeout, additionalOptions) {
    return (VietMeetJS.createLocalTracks({
        cameraDeviceId: deviceId,
        devices: [type],
        micDeviceId: deviceId,
        timeout,
        ...additionalOptions
    })
        .then(([vietmeetLocalTrack]) => vietmeetLocalTrack));
}
/**
 * Determines whether analytics is enabled in a specific redux {@code store}.
 *
 * @param {IStateful} stateful - The redux store, state, or
 * {@code getState} function.
 * @returns {boolean} If analytics is enabled, {@code true}; {@code false},
 * otherwise.
 */
export function isAnalyticsEnabled(stateful) {
    const { disableThirdPartyRequests, analytics = {} } = toState(stateful)['features/base/config'];
    return !(disableThirdPartyRequests || analytics.disabled);
}
/**
 * Determines whether a specific {@link VietMeetConferenceErrors} instance
 * indicates a fatal {@link VietMeetConference} error.
 *
 * FIXME Figure out the category of errors defined by the function and describe
 * that category. I've currently named the category fatal because it appears to
 * be used in the cases of unrecoverable errors that necessitate a reload.
 *
 * @param {Error|string} error - The {@code VietMeetConferenceErrors} instance to
 * categorize/classify or an {@link Error}-like object.
 * @returns {boolean} If the specified {@code VietMeetConferenceErrors} instance
 * indicates a fatal {@code VietMeetConference} error, {@code true}; otherwise,
 * {@code false}.
 */
export function isFatalVietMeetConferenceError(error) {
    if (typeof error !== 'string') {
        error = error.name; // eslint-disable-line no-param-reassign
    }
    return (error === VietMeetConferenceErrors.FOCUS_DISCONNECTED
        || error === VietMeetConferenceErrors.FOCUS_LEFT
        || error === VietMeetConferenceErrors.ICE_FAILED
        || error === VietMeetConferenceErrors.OFFER_ANSWER_FAILED
        || error === VietMeetConferenceErrors.VIDEOBRIDGE_NOT_AVAILABLE);
}
/**
 * Determines whether a specific {@link VietMeetConnectionErrors} instance
 * indicates a fatal {@link VietMeetConnection} error.
 *
 * FIXME Figure out the category of errors defined by the function and describe
 * that category. I've currently named the category fatal because it appears to
 * be used in the cases of unrecoverable errors that necessitate a reload.
 *
 * @param {Error|string} error - The {@code VietMeetConnectionErrors} instance to
 * categorize/classify or an {@link Error}-like object.
 * @returns {boolean} If the specified {@code VietMeetConnectionErrors} instance
 * indicates a fatal {@code VietMeetConnection} error, {@code true}; otherwise,
 * {@code false}.
 */
export function isFatalVietMeetConnectionError(error) {
    if (typeof error !== 'string') {
        error = error.name; // eslint-disable-line no-param-reassign
    }
    return (error === VietMeetConnectionErrors.CONNECTION_DROPPED_ERROR
        || error === VietMeetConnectionErrors.OTHER_ERROR
        || error === VietMeetConnectionErrors.SERVER_ERROR);
}
